import { createAsyncThunk } from '@reduxjs/toolkit';

import ApiClient from 'api/axiosClient';
import salesApiClient from 'api/salesApiClient';
import { buildQueryString } from 'utils';
import { notifyError, notifySuccess } from 'utils/notifyConfig';

// ==================== LEAD SOURCES ====================

export const getLeadSources = createAsyncThunk(
  'newsales/lead-sources/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
      };

      if (params.status) {
        queryParams.status = params.status;
      }

      if (params.search) {
        queryParams.name = params.search;
      }

      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(`lead-sources${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createLeadSource = createAsyncThunk(
  'newsales/lead-sources/create',
  async (leadSourceData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('lead-sources', leadSourceData, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Lead source created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateLeadSource = createAsyncThunk(
  'newsales/lead-sources/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`lead-sources/${id}`, data, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Lead source updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteLeadSource = createAsyncThunk(
  'newsales/lead-sources/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`lead-sources/${id}`, {});
      notifySuccess('Lead source deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== PRODUCTS (Future Implementation) ====================

export const getProducts = createAsyncThunk(
  'newsales/products/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(`products${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'newsales/products/create',
  async (productData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('products', productData, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Product created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'newsales/products/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`products/${id}`, data, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Product updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'newsales/products/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`products/${id}`, {});
      notifySuccess('Product deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== OFFERS (Future Implementation) ====================

export const getOffers = createAsyncThunk(
  'newsales/offers/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(`offers${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createOffer = createAsyncThunk(
  'newsales/offers/create',
  async (offerData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('offers', offerData, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Offer created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateOffer = createAsyncThunk(
  'newsales/offers/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`offers/${id}`, data, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Offer updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteOffer = createAsyncThunk(
  'newsales/offers/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`offers/${id}`, {});
      notifySuccess('Offer deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== COMPETITORS ====================

export const getCompetitors = createAsyncThunk(
  'newsales/competitors/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(`competitors${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createCompetitor = createAsyncThunk(
  'newsales/competitors/create',
  async (competitorData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('competitors', competitorData, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Competitor created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCompetitor = createAsyncThunk(
  'newsales/competitors/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`competitors/${id}`, data, {
        'Content-Type': 'multipart/form-data',
      });
      notifySuccess('Competitor updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteCompetitor = createAsyncThunk(
  'newsales/competitors/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`competitors/${id}`, {});
      notifySuccess('Competitor deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== COUNTRIES ====================

export const getCountries = createAsyncThunk(
  'newsales/countries/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(
        `addresses/countries${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createCountry = createAsyncThunk(
  'newsales/countries/create',
  async (countryData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('addresses/countries', countryData, {}, {});
      notifySuccess('Country created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCountry = createAsyncThunk(
  'newsales/countries/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`addresses/countries/${id}`, data, {});
      notifySuccess('Country updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteCountry = createAsyncThunk(
  'newsales/countries/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/countries/${id}`, {});
      notifySuccess('Country deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== Regions ====================

export const getAdministrativeRegions = createAsyncThunk(
  'newsales/administrative-regions/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = { ...params };
      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(
        `addresses/districts${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAdministrativeRegion = createAsyncThunk(
  'newsales/administrative-regions/create',
  async (regionData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('addresses/districts', regionData, {}, {});
      notifySuccess('Region created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdministrativeRegion = createAsyncThunk(
  'newsales/administrative-regions/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`addresses/districts/${id}`, data, {});
      notifySuccess('Region updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAdministrativeRegion = createAsyncThunk(
  'newsales/administrative-regions/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/districts/${id}`, {});
      notifySuccess('Region deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== CITIES ====================

export const getCities = createAsyncThunk(
  'newsales/cities/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = { ...params };
      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(`addresses/cities${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createCity = createAsyncThunk(
  'newsales/cities/create',
  async (cityData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('addresses/cities', cityData, {}, {});
      notifySuccess('City created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCity = createAsyncThunk(
  'newsales/cities/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`addresses/cities/${id}`, data, {});
      notifySuccess('City updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteCity = createAsyncThunk(
  'newsales/cities/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/cities/${id}`, {});
      notifySuccess('City deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== ADMINISTRATIVE DISTRICTS ====================

export const getAdministrativeDistricts = createAsyncThunk(
  'newsales/administrative-districts/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = { ...params };
      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(
        `addresses/administrative-regions${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAdministrativeDistrict = createAsyncThunk(
  'newsales/administrative-districts/create',
  async (districtData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        'addresses/administrative-regions',
        districtData,
        {},
        {}
      );
      notifySuccess('Administrative district created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdministrativeDistrict = createAsyncThunk(
  'newsales/administrative-districts/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(
        `addresses/administrative-regions/${id}`,
        data,
        {}
      );
      notifySuccess('Administrative district updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAdministrativeDistrict = createAsyncThunk(
  'newsales/administrative-districts/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/administrative-regions/${id}`, {});
      notifySuccess('Administrative district deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== STREETS ====================

export const getStreets = createAsyncThunk(
  'newsales/streets/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { countryId, districtId, cityId, ...restParams } = params;
      if (!countryId || !districtId || !cityId) {
        return rejectWithValue('Country ID, District ID, and City ID are required');
      }
      const queryParams = { countryId, districtId, cityId, ...restParams };
      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(
        `addresses/streets${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createStreet = createAsyncThunk(
  'newsales/streets/create',
  async (streetData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('addresses/streets', streetData, {}, {});
      notifySuccess('Street created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateStreet = createAsyncThunk(
  'newsales/streets/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`addresses/streets/${id}`, data, {});
      notifySuccess('Street updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteStreet = createAsyncThunk(
  'newsales/streets/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/streets/${id}`, {});
      notifySuccess('Street deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== BUILDINGS ====================

export const getBuildings = createAsyncThunk(
  'newsales/buildings/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        countryId,
        districtId,
        cityId,
        streetId,
        administrativeDistrictId,
        ...restParams
      } = params;
      if (!countryId || !districtId || !cityId || !streetId) {
        return rejectWithValue('Country ID, District ID, City ID, and Street ID are required');
      }
      const queryParams = { countryId, districtId, cityId, streetId, ...restParams };
      if (administrativeDistrictId) {
        queryParams.administrativeDistrictId = administrativeDistrictId;
      }
      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(
        `addresses/building-houses${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createBuilding = createAsyncThunk(
  'newsales/buildings/create',
  async (buildingData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        'addresses/building-houses',
        buildingData,
        {},
        {}
      );
      notifySuccess('Building created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBuilding = createAsyncThunk(
  'newsales/buildings/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`addresses/building-houses/${id}`, data, {});
      notifySuccess('Building updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteBuilding = createAsyncThunk(
  'newsales/buildings/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`addresses/building-houses/${id}`, {});
      notifySuccess('Building deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== TEAMS ====================

export const getTeamsAll = createAsyncThunk(
  'newsales/teams/getAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { limit = 50, offset = 0, search = '' } = params;
      const queryParams = { limit, offset };
      if (search) {
        queryParams.search = search;
      }
      const query = buildQueryString(queryParams);
      const data = await ApiClient.get(`teams/all${query ? `?${query}` : ''}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==================== BRANCHES ====================

export const getBranchesAll = createAsyncThunk(
  'newsales/branches/getAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { limit = 50, offset = 0, search = '' } = params;
      const queryParams = { limit, offset };
      if (search) {
        queryParams.search = search;
      }
      const query = buildQueryString(queryParams);
      const data = await ApiClient.get(`branches${query ? `?${query}` : ''}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==================== DEPARTMENTS ====================

export const getDepartmentsAll = createAsyncThunk(
  'newsales/departments/getAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { limit = 50, offset = 0, search = '' } = params;
      const queryParams = { limit, offset };
      if (search) {
        queryParams.search = search;
      }
      const query = buildQueryString(queryParams);
      const data = await ApiClient.get(`departments${query ? `?${query}` : ''}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==================== PRIVILEGE ASSIGNMENTS ====================

export const assignPrivilege = createAsyncThunk(
  'newsales/privilege-assignments/assign',
  async (assignmentData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('privilege-assignments/bulk', assignmentData);
      notifySuccess('Privilege assigned successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deletePrivilegeAssignment = createAsyncThunk(
  'newsales/privilege-assignments/delete',
  async ({ privilegeId, level, targetUuid }, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`privilege-assignments/by-privilege/${privilegeId}`, {
        data: { level, targetUuid },
      });
      notifySuccess('Assignment removed successfully');
      return targetUuid;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getPrivilegeAssignmentsByPrivilege = createAsyncThunk(
  'newsales/privilege-assignments/byPrivilege',
  async ({ privilegeId, level = 'user' }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(
        `privilege-assignments/by-privilege/${privilegeId}?level=${level}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==================== WORKFLOW STATUSES (Future Implementation) ====================

export const getWorkflowStatuses = createAsyncThunk(
  'newsales/workflow-statuses/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(
        `workflow-statuses${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createWorkflowStatus = createAsyncThunk(
  'newsales/workflow-statuses/create',
  async (statusData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('workflow-statuses', statusData, {}, {});
      notifySuccess('Workflow status created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateWorkflowStatus = createAsyncThunk(
  'newsales/workflow-statuses/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`workflow-statuses/${id}`, data, {});
      notifySuccess('Workflow status updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteWorkflowStatus = createAsyncThunk(
  'newsales/workflow-statuses/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`workflow-statuses/${id}`, {});
      notifySuccess('Workflow status deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== USER PRIVILEGES ====================

export const getUserPrivileges = createAsyncThunk(
  'newsales/user-privileges/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 9,
      };

      if (params.status && params.status !== 'all') {
        queryParams.status = params.status;
      } else if (params.status === 'all') {
        queryParams.status = 'all';
      }

      if (params.search) {
        queryParams.name = params.search;
      }

      const query = buildQueryString(queryParams);
      const data = await salesApiClient.get(`user-privileges${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createUserPrivilege = createAsyncThunk(
  'newsales/user-privileges/create',
  async (privilegeData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('user-privileges', privilegeData, {}, {});
      notifySuccess('User privilege created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserPrivilege = createAsyncThunk(
  'newsales/user-privileges/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`user-privileges/${id}`, data, {});
      notifySuccess('User privilege updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteUserPrivilege = createAsyncThunk(
  'newsales/user-privileges/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`user-privileges/${id}`, {});
      notifySuccess('User privilege deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== NOTIFICATIONS ====================

export const getNotificationRules = createAsyncThunk(
  'newsales/notifications/get',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get('notification-rules', {});
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createNotificationRule = createAsyncThunk(
  'newsales/notifications/create',
  async (notificationData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('notification-rules', notificationData, {}, {});
      notifySuccess('Notification created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateNotificationRule = createAsyncThunk(
  'newsales/notifications/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`notification-rules/${id}`, data, {});
      notifySuccess('Notification updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteNotificationRule = createAsyncThunk(
  'newsales/notifications/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`notification-rules/${id}`, {});
      notifySuccess('Notification deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleNotificationRule = createAsyncThunk(
  'newsales/notifications/toggle',
  async (id, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.patch(
        `notification-rules/${id}/toggle`,
        {},
        {},
        {}
      );
      notifySuccess('Notification status updated');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== LEADS ====================

export const getLeadById = createAsyncThunk(
  'newsales/leads/getById',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`leads/${leadId}`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createLeadB2B = createAsyncThunk(
  'newsales/leads/createB2B',
  async (leadData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('leads/b2b', leadData, {}, {});
      notifySuccess('B2B Lead created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createLeadB2C = createAsyncThunk(
  'newsales/leads/createB2C',
  async (leadData, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('leads/b2c', leadData, {}, {});
      notifySuccess('B2C Lead created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createLeadNote = createAsyncThunk(
  'newsales/leads/createNote',
  async ({ leadId, noteData }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(`leads/${leadId}/notes`, noteData, {}, {});
      notifySuccess('Note created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getLeadNotes = createAsyncThunk(
  'newsales/leads/getNotes',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`leads/${leadId}/notes`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getLeadHistory = createAsyncThunk(
  'newsales/leads/getHistory',
  async ({ leadId }, { rejectWithValue }) => {
    try {
      const queryParams = {};
      queryParams.action = 'status_changed';

      const query = buildQueryString(queryParams);

      const data = await salesApiClient.get(
        `leads/${leadId}/history${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateLead = createAsyncThunk(
  'newsales/leads/update',
  async ({ leadId, leadData }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.put(`leads/${leadId}`, leadData, {});
      notifySuccess('Lead updated successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateLeadPriority = createAsyncThunk(
  'newsales/leads/updatePriority',
  async ({ leadId, priority }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.patch(
        `leads/${leadId}/priority`,
        { priority: priority.toUpperCase() },
        {},
        {}
      );
      notifySuccess('Lead priority updated successfully');
      return { leadId, priority, ...data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getLeadsKanban = createAsyncThunk(
  'newsales/leads/getKanban',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(`leads/kanban${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getLeadsKanbanColumn = createAsyncThunk(
  'newsales/leads/getKanbanColumn',
  async ({ workflowStatusId, params = {} }, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(
        `leads/kanban/column/${workflowStatusId}${query ? `?${query}` : ''}`,
        {}
      );
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkChangeLeadStatus = createAsyncThunk(
  'newsales/leads/bulkChangeStatus',
  async ({ leadIds, workflowStatusId }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        'leads/bulk/change-status',
        { leadIds, workflowStatusId },
        {},
        {}
      );
      if (data.success === false || (data.failedCount && data.failedCount > 0)) {
        const failedResults = (data.results || []).filter((r) => !r.success);
        if (failedResults.length > 0) {
          failedResults.forEach((r) => {
            notifyError(r.error || 'Failed to update lead status');
          });
        } else {
          notifyError('Failed to update lead status');
        }
        return rejectWithValue(data);
      }
      notifySuccess('Lead status updated successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkAssignLeads = createAsyncThunk(
  'newsales/leads/bulkAssign',
  async ({ leadIds, assigneeUuid }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        'leads/bulk/assign',
        { leadIds, assigneeUuid },
        {},
        {}
      );
      notifySuccess('Lead assigned successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkReassignLeads = createAsyncThunk(
  'newsales/leads/bulkReassign',
  async ({ leadIds, newAssigneeUuid }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        'leads/bulk/reassign',
        { leadIds, newAssigneeUuid },
        {},
        {}
      );
      notifySuccess('Lead reassigned successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkUnassignLeads = createAsyncThunk(
  'newsales/leads/bulkUnassign',
  async ({ leadIds }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post('leads/bulk/unassign', { leadIds }, {}, {});
      notifySuccess('Lead unassigned successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getLeadsPool = createAsyncThunk(
  'newsales/leads/getPool',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params);
      const data = await salesApiClient.get(`leads${query ? `?${query}` : ''}`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getPassportData = createAsyncThunk(
  'newsales/leads/passport-data/get',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`leads/${leadId}/passport-date`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== ORDER OFFERS ====================

export const createOrderOffer = createAsyncThunk(
  'newsales/order-offers/create',
  async ({ leadId, orderData }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        `leads/${leadId}/order-offers`,
        orderData,
        {},
        {}
      );
      notifySuccess('Order created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getOrderOffers = createAsyncThunk(
  'newsales/order-offers/get',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`leads/${leadId}/order-offers`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateOrderOffer = createAsyncThunk(
  'newsales/order-offers/update',
  async ({ leadId, orderOfferId, orderData }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.put(
        `leads/${leadId}/order-offers/${orderOfferId}`,
        orderData,
        {}
      );
      notifySuccess('Order updated successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteOrderOffer = createAsyncThunk(
  'newsales/order-offers/delete',
  async ({ leadId, orderOfferId }, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`leads/${leadId}/order-offers/${orderOfferId}`, {});
      notifySuccess('Order deleted successfully');
      return { orderOfferId };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const approveOrderOffer = createAsyncThunk(
  'newsales/order-offers/approve',
  async ({ leadId, orderOfferId }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.patch(
        `leads/${leadId}/order-offers/${orderOfferId}/approve`,
        {},
        {},
        {}
      );
      notifySuccess('Order approved successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const rejectOrderOffer = createAsyncThunk(
  'newsales/order-offers/reject',
  async ({ leadId, orderOfferId }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.patch(
        `leads/${leadId}/order-offers/${orderOfferId}/reject`,
        {},
        {},
        {}
      );
      notifySuccess('Order rejected successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const addLeadOffer = createAsyncThunk(
  'newsales/lead-offers/add',
  async ({ leadId, offerIds }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(`leads/${leadId}/bulk-assign-offers`, {
        offerIds,
      });
      if (data.added > 0 && data.skipped === 0) {
        notifySuccess(`${data.added} offer(s) added successfully`);
      } else if (data.added > 0 && data.skipped > 0) {
        notifySuccess(`${data.added} offer(s) added. ${data.message}`);
      } else {
        notifyError(data.message);
      }
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteLeadOffer = createAsyncThunk(
  'newsales/lead-offers/delete',
  async ({ leadId, offerId }, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`leads/${leadId}/offers/${offerId}`, {});
      notifySuccess('Offer deleted successfully');
      return { offerId };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== FOLLOW-UPS ====================

export const createFollowUp = createAsyncThunk(
  'newsales/follow-ups/create',
  async ({ leadId, followUpData }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.post(
        `leads/${leadId}/follow-ups`,
        followUpData,
        {},
        {}
      );
      notifySuccess('Follow-up created successfully');
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getFollowUps = createAsyncThunk(
  'newsales/follow-ups/get',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.get(`leads/${leadId}/follow-ups`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const searchFollowUpRecipients = createAsyncThunk(
  'newsales/follow-up-recipients/search',
  async (searchQuery, { rejectWithValue }) => {
    try {
      const query = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const data = await salesApiClient.get(`follow-up-recipients${query}`, {});
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAssigned = createAsyncThunk(
  'newsales/assigned/get',
  async (search = '', { rejectWithValue }) => {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await ApiClient.get(`users/dropdown${query}`, {});

      return res.users;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAssigned = createAsyncThunk(
  'newsales/assigned/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.post('users/assigned', data, {}, {});
      notifySuccess('Assigned created successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAssigned = createAsyncThunk(
  'newsales/assigned/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.put(`users/assigned/${id}`, data, {});
      notifySuccess('Assigned updated successfully');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAssigned = createAsyncThunk(
  'newsales/assigned/delete',
  async (id, { rejectWithValue }) => {
    try {
      await salesApiClient.del(`users/assigned/${id}`, {});
      notifySuccess('Assigned deleted successfully');
      return { id };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const completeFollowUp = createAsyncThunk(
  'newsales/completeFollowUp',
  async ({ leadId, followUpId }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.patch(
        `/leads/${leadId}/follow-ups/${followUpId}/complete`
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getContractCredentials = createAsyncThunk(
  'newsales/customer/contract-credentials',
  async (leadUuid, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.get(
        `erp/customers/${leadUuid}/contract-credentials`
      );
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const getLinkedProcesses = createAsyncThunk(
  'newsales/customer/linked-processes',
  async (leadUuid, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.get(`erp/customers/${leadUuid}/linked-processes`);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const replaceLeadOffer = createAsyncThunk(
  'newsales/leads/replace-offer',
  async ({ leadId, deleteOfferId, newOfferId, count, newProductId }, { rejectWithValue }) => {
    try {
      const data = await salesApiClient.patch(`leads/${leadId}/replace-offer`, {
        deleteOfferId,
        newOfferId,
        count,
        newProductId,
      });
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const checkAddress = createAsyncThunk(
  'newsales/leads/check-address',
  async (data, { rejectWithValue }) => {
    try {
      const result = await salesApiClient.post('leads/check-address', data);
      return result;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const attachVivaProcess = createAsyncThunk(
  'newsales/lead/attachVivaProcess',
  async ({ leadId, processId }, { rejectWithValue }) => {
    try {
      const response = await salesApiClient.patch(`leads/${leadId}/viva-process`, {
        vivaProcess: processId,
      });
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);
